import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';

const processSteps = [
  { title: 'Inspeção Técnica', desc: 'Avaliação visual e dimensional do equipamento para identificar condições gerais e pontos críticos.' },
  { title: 'Diagnóstico de Falhas', desc: 'Análise técnica detalhada para identificar causas raiz de desgaste, falhas mecânicas e perda de eficiência.' },
  { title: 'Desmontagem Técnica', desc: 'Desmontagem controlada com identificação e catalogação de todos os componentes.' },
  { title: 'Recuperação Mecânica', desc: 'Recuperação de componentes mecânicos com ajustes, usinagens e processos especializados.' },
  { title: 'Recuperação Hidráulica', desc: 'Recuperação de cilindros, bombas, motores e sistemas hidráulicos integrados ao equipamento.' },
  { title: 'Recuperação Estrutural', desc: 'Recuperação ou coordenação técnica de reparos estruturais, soldagens e reforços.' },
  { title: 'Substituição de Componentes', desc: 'Troca de peças críticas, vedações, componentes desgastados e itens fora de especificação.' },
  { title: 'Montagem', desc: 'Montagem técnica completa com ajustes, alinhamento e lubrificação conforme especificações técnicas.' },
  { title: 'Testes Operacionais', desc: 'Validação funcional e operacional do equipamento com testes de carga, movimento e segurança.' },
];

const problems = [
  'Desgaste Excessivo',
  'Falhas Mecânicas',
  'Vazamentos Hidráulicos',
  'Trincas Estruturais',
  'Corrosão',
  'Falhas Operacionais',
  'Perda de Eficiência',
  'Baixa Confiabilidade',
];

const integratedServices = [
  'Recuperação de Componentes Mecânicos',
  'Recuperação de Componentes Hidráulicos',
  'Recuperação Estrutural',
  'Usinagens Especiais',
  'Recuperação de Eixos',
  'Recuperação de Carcaças',
  'Tratamentos Superficiais',
  'Pintura Industrial',
];

const equipmentTypes = [
  'Transportadores Industriais',
  'Equipamentos de Pátio de Madeira',
  'Sistemas Hidráulicos Industriais',
  'Gruas Florestais',
  'Equipamentos da Área de Secagem',
  'Ventiladores Industriais',
  'Exaustores Industriais',
  'Sistemas de Transporte e Movimentação Industrial',
  'Equipamentos Rotativos Industriais',
  'Equipamentos Auxiliares de Produção',
];

const segments = [
  'Papel e Celulose',
  'Florestal',
  'Logística Industrial',
  'Portos',
  'Mineração',
  'Energia',
];

export default function RecuperacaoCelulosePage() {
  useSeo({
    title: 'Recuperação de Equipamentos para Papel e Celulose | Oficina Especializada | Pilar',
    description: 'Recuperação de equipamentos para papel e celulose com diagnóstico técnico, recuperação mecânica, hidráulica e estrutural. Confiabilidade operacional para operações críticas de celulose e florestal.',
    keywords: 'recuperação de equipamentos para papel e celulose, recuperação de equipamentos de celulose, manutenção industrial celulose, transportadores industriais, roscas transportadoras, movimentação de cavacos, equipamentos de pátio de madeira, Grupo Pilar, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/recuperacao-equipamentos-papel-celulose',
    ogUrl: 'https://www.pilarmanutencao.com.br/recuperacao-equipamentos-papel-celulose',
    ogImage: ASSETS.oficina,
  });

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/OFICINA/RECUPERACAO-EQUIPAMENTOS-PAPEL-E-CELULOSE/HERO/HERO-EQUIPAMENTOS-PAPEL-E-CELULOSE.webp"
              alt="Recuperação de equipamentos para papel e celulose na oficina especializada Grupo Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Oficina Especializada</span>
              </div>
              <h1 className="font-display text-4xl md:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Recuperação</span> de Equipamentos para Papel e Celulose
              </h1>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed">
                Recuperamos equipamentos utilizados em operações críticas da indústria de papel e celulose, atuando desde o diagnóstico técnico até a entrega final do equipamento pronto para operação.
              </p>
            </div>
          </div>
        </section>

        {/* O QUE FAZEMOS */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">O Que Fazemos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {processSteps.map((step, index) => (
                <div
                  key={index}
                  className="group bg-gp-graphite hover:bg-gp-graphite-lighter p-6 rounded-lg transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gp-yellow/20"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                    <span className="text-gp-yellow group-hover:text-gp-graphite font-display font-bold text-sm transition-colors duration-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PRINCIPAIS PROBLEMAS CORRIGIDOS */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Principais Problemas Corrigidos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {problems.map((problem, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-gp-graphite border border-white/10 rounded-lg px-5 py-4 hover:border-gp-yellow/30 transition-colors duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{problem}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SOLUÇÃO COMPLETA */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Solução Completa para Recuperação de Equipamentos</h2>
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                A Pilar coordina todas as etapas necessárias para recuperação dos equipamentos, garantindo qualidade, rastreabilidade e responsabilidade técnica centralizada.
              </p>
              <p className="text-gray-400 leading-relaxed">
                O cliente recebe o equipamento pronto para retorno à operação, reduzindo riscos de novas paradas e aumentando a disponibilidade dos ativos.
              </p>
            </div>
          </div>
        </section>

        {/* SERVIÇOS ESPECIALIZADOS INTEGRADOS */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Serviços Especializados Integrados ao Processo</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  Quando necessário, a Pilar coordena e acompanha tecnicamente processos especializados executados por parceiros homologados, garantindo qualidade e conformidade com os requisitos do projeto.
                </p>
                <p className="text-gray-400 leading-relaxed">
                  Esses serviços especializados são integrados ao processo de recuperação, garantindo que o cliente receba o equipamento completo e validado para retorno à operação.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {integratedServices.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-gp-graphite border border-white/10 rounded-lg px-4 py-3 hover:border-gp-yellow/30 transition-colors duration-300"
                  >
                    <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <span className="text-gray-300 text-sm font-medium">{service}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* EQUIPAMENTOS ATENDIDOS */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Equipamentos Atendidos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {equipmentTypes.map((eq, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-hammer-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{eq}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SEGMENTOS ATENDIDOS */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Segmentos Atendidos</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((segment, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{segment}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Atendimento em Aracruz */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="relative rounded-xl overflow-hidden border border-gp-yellow/20">
                <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/10 to-gp-graphite"></div>
                <div className="relative z-10 p-8 md:p-10">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center">
                      <i className="ri-map-pin-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h2 className="font-display text-xl font-bold text-white uppercase tracking-wider">Atendimento em Aracruz</h2>
                  </div>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    A Pilar oferece recuperação de equipamentos para papel e celulose com estrutura própria em Aracruz e região, atendendo as indústrias de celulose, florestal, portos e mineração.
                  </p>
                  <Link to="/recuperacao-equipamentos-celulose-aracruz" className="inline-flex items-center gap-2 text-gp-yellow hover:text-white transition-colors duration-300 font-semibold text-sm uppercase tracking-wider">
                    <span>Conheça nossa atuação em Aracruz</span>
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa recuperar um equipamento da sua operação?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Solicite uma avaliação técnica especializada e receba um diagnóstico completo do seu equipamento.
                </p>
                <a
                  href="https://wa.me/5527993092922"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span>Solicitar Orçamento</span>
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Home */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para a Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}